import { CellClickedEvent, CellValueChangedEvent, ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { MutableRefObject, useCallback, useEffect, useMemo } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Excel from 'src/components/common/gridWrapper/Grid_Excel';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { createCellCssClassRules, GridCellConfig } from 'src/utils/grid/GridHandlerContent';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultTQC1020_R01HeaderDto, TQC1020_R01HeaderDto } from './Dto_TQC1020_R01';
import { useTQC1020_R01Store } from './Store_TQC1020_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

interface Props {
	gridRef: MutableRefObject<AgGridReact<TQC1020_R01HeaderDto> | null>;
}

const COLUMN_STATE_KEY = 'TQC1020_R01-grid-column-state';

export const Table_TQC1020_R01 = ({ gridRef }: Props) => {
	const {
		TQC1020_R01List,
		setTQC1020_R01List,
		selectedTQC1020_R01,
		setColumnState,
		columnState,
		setSelectedTQC1020_R01,
		scrollTop,
		setScrollTop,
		setDetailScrollTop,
	} = useTQC1020_R01Store();

	const { inspectionResultList } = useCommonCodeStore();

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent<TQC1020_R01HeaderDto>({
		gridRef,
		key: 'valueIndex',
		gridData: TQC1020_R01List,
		defaultGridData: defaultTQC1020_R01HeaderDto,
		selectedRow: selectedTQC1020_R01,
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: [],
		pointerCells: ['inspectionResult'],
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ['compNm', 'transportDt', 'supplierCustomNm', 'itemCd', 'itemNm', 'itemSpec', 'locationNm', 'unit', 'inspectionResult'],
	}), []);

	const { disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns } = useMemo(
		() => createCellCssClassRules(cssCellConfig), [cssCellConfig],
	);

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compNm: '사업장',
		transportDt: '입고일',
		supplierCustomNm: '거래처',
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		locationNm: '보관위치',
		qty: '입고량',
		unit: '단위',
		inspectionResult: '검사결과',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const handleCellValueChanged = useCallback(
		(e: CellValueChangedEvent<TQC1020_R01HeaderDto>) => {
			if (e.colDef.field !== 'inspectionResult') return;
			if (!e.data) return;
			if (TQC1020_R01List.length === 0) return;
			if (String(e.newValue ?? '') === String(e.oldValue ?? '')) return;

			const newInspectionResult = String(e.newValue ?? '');

			setTQC1020_R01List(
				TQC1020_R01List.map((row) =>
					row.transportNo === e.data!.transportNo && row.itemNo === e.data!.itemNo
						? {
							...row,
							inspectionResult: newInspectionResult,
							hasInspectResult: newInspectionResult !== '',
							isModified: true,
						}
						: row
				),
			);
		},
		[TQC1020_R01List, setTQC1020_R01List],
	);

	const handleRowClicked = useCallback(
		(e: RowClickedEvent<TQC1020_R01HeaderDto>) => {
			if (e.node.rowPinned === 'bottom') return;
			if (!e.data) return;
			setSelectedTQC1020_R01(e.data);
			setDetailScrollTop(null);
		},
		[setSelectedTQC1020_R01, setDetailScrollTop],
	);

	const handleCellClicked = useCallback(
		(e: CellClickedEvent<TQC1020_R01HeaderDto>) => {
			if (e.column.getColId() !== 'inspectionResult') return;
			if (!e.data || e.node.rowPinned) return;
			setSelectedTQC1020_R01(e.data);
			setDetailScrollTop(null);

			setTimeout(() => {
				e.api.startEditingCell({
					rowIndex: e.rowIndex!,
					colKey: 'inspectionResult',
				});

				setTimeout(() => {
					const selectEl = document.querySelector<HTMLSelectElement>(
						'.ag-select-cell-editor select',
					);
					if (selectEl) {
						selectEl.focus();
						selectEl.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
					}
				}, 0);
			}, 0);
		},
		[setSelectedTQC1020_R01, setDetailScrollTop],
	);

	const baseColumnDefs = useMemo<ColDef<TQC1020_R01HeaderDto>[]>(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: '사업장', colId: 'compNm', field: 'compNm', width: 90,
				cellStyle: (p) => getCellStyle(p, 'compNm'),
			},
			{
				headerName: '입고일', colId: 'transportDt', field: 'transportDt', width: 110,
				cellStyle: (p) => getCellStyle(p, 'transportDt'),
			},
			{
				headerName: '거래처', colId: 'supplierCustomNm', field: 'supplierCustomNm', width: 130,
				cellStyle: (p) => getCellStyle(p, 'supplierCustomNm'),
			},
			{
				headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 110,
				cellStyle: (p) => getCellStyle(p, 'itemCd'),
			},
			{
				headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 200,
				minWidth: 140,
				cellStyle: (p) => getCellStyle(p, 'itemNm'),
			},
			{
				headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 120,
				cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			},
			{
				headerName: '보관위치', colId: 'locationNm', field: 'locationNm', width: 120,
				cellStyle: (p) => getCellStyle(p, 'locationNm'),
			},
			{
				headerName: '입고량', colId: 'qty', field: 'qty', width: 100,
				valueFormatter: (params) =>
					params.value === undefined || params.value === null
						? ''
						: Number(params.value).toLocaleString(),
				cellStyle: (p) => getCellStyle(p, 'qty'),
			},
			{
				headerName: '단위', colId: 'unit', field: 'unit', width: 70,
				cellStyle: (p) => getCellStyle(p, 'unit'),
			},
			{
				headerName: '검사결과', colId: 'inspectionResult', field: 'inspectionResult', width: 110,
				editable: true,
				cellStyle: (p) => getCellStyle(p, 'inspectionResult'),
				cellEditor: 'agSelectCellEditor',
				cellEditorParams: {
					values: inspectionResultList.map((c) => c.minorCd),
				},
				valueFormatter: (params) => {
					if (!params.value) return '';
					const found = inspectionResultList.find(
						(c) => c.minorCd === params.value,
					);
					return found ? found.codeNm : params.value;
				},
			},
		],
		[inspectionResultList, getCellStyle],
	);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const cellCssDefaultColDef = useMemo(() => {
		const selectedList =
			selectedTQC1020_R01?.valueIndex != null ? [selectedTQC1020_R01.valueIndex] : [];
		const baseRules = createCellClassRules<TQC1020_R01HeaderDto>({
			idKey: 'valueIndex',
			selectedList,
			deletedList: [],
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [
		selectedTQC1020_R01?.valueIndex,
		createCellClassRules,
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	]);

	const colDefs = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<TQC1020_R01HeaderDto>({
			baseColumnDefs: baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRowData = useMemo(
		() => ([{
			compNm: `${TQC1020_R01List.length.toLocaleString()}건`,
			transportDt: undefined,
			supplierCustomNm: '',
			itemCd: '',
			itemNm: '',
			itemSpec: '',
			unit: '',
			inspectionResult: '',
			hasInspectResult: false,
			inserted: false,
		} as unknown as TQC1020_R01HeaderDto]),
		[TQC1020_R01List.length],
	);

	useEffect(() => { ensureSelectedRow(); }, [TQC1020_R01List, selectedTQC1020_R01]);

	return (
		<GridContextWrapper<TQC1020_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setTQC1020_R01List}
			initSelected={() => setSelectedTQC1020_R01(undefined)}
			defaultRowData={defaultTQC1020_R01HeaderDto}
			baseColumnDefs={columnState ? colDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<TQC1020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={colDefs}
				defaultColDef={cellCssDefaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<TQC1020_R01HeaderDto>({
					idKey: 'valueIndex',
					selectedList: selectedTQC1020_R01 ? [selectedTQC1020_R01.valueIndex] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClicked}
				onCellClicked={handleCellClicked}
				onCellValueChanged={handleCellValueChanged}
				pinnedBottomRowData={pinnedBottomRowData}
				getRowStyle={(params) => {
					if (params.data?.hasInspectResult) {
						return { backgroundColor: '#e8f5e9' };
					}
					return undefined;
				}}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={(state) => setColumnState(state)}
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
